import {
    postAddBidderAddress,
    postAddBidderAddressV2,
    postUpdateBidderAddress,
    postUpdateBidderAddressV2,
    postUpdateProfile,
    UpdateProfileRequest,
} from '@/redux/modules/account/updateAccount/updateAccount.api';
import {
    UPDATE_ACCOUNT_FAIL,
    UPDATE_ACCOUNT_REQUEST,
    UPDATE_ACCOUNT_SUCCESS,
    UPDATE_ACCOUNT_SUGGESTION,
    UPDATE_ACCOUNT_TOAST_MESSAGE,
    UpdateAccountSuggestionAction,
} from '@/redux/modules/account/updateAccount/updateAccount.types';
import { fetchBidderDataIfNeeded, storeSavedAddress } from '@/redux/modules/account/user/user.actions';
import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { dismissModal, openAddressVerifyModal } from '@/redux/modules/modal';
import { AppDispatch, AppGetState } from '@/redux/store';
import { BidderAddress } from '@/types/BidderAddresses';

import { getDeployment } from '../../config';

export const updateAccountDetails =
    (updatedProfile: UpdateProfileRequest) => async (dispatch: AppDispatch, getState: AppGetState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            dispatch({
                type: UPDATE_ACCOUNT_REQUEST,
            });

            const response = await postUpdateProfile({
                authToken,
                deployment,
                profile: updatedProfile,
            });

            dispatch({
                meta: { actionTime: Date.now() },
                payload: response,
                // eslint-disable-next-line no-undef
                type: UPDATE_ACCOUNT_SUCCESS,
            });

            return dispatch(fetchBidderDataIfNeeded({ force: true }));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_ACCOUNT_FAIL,
            });
        }
    };

export const updateBidderAddress =
    (bidderID: number, address: BidderAddress, toastMessage: string, verify = false, shouldOpenModal = true) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        try {
            dispatch({
                type: UPDATE_ACCOUNT_REQUEST,
            });

            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            const response = await postUpdateBidderAddress({
                address,
                authToken,
                bidderID,
                deployment,
                verify,
            });
            if (!Array.isArray(response.payload)) {
                dispatch({
                    meta: { actionTime: Date.now() },
                    payload: response,
                    type: UPDATE_ACCOUNT_SUGGESTION,
                });
                if (shouldOpenModal) {
                    dispatch(
                        openAddressVerifyModal({
                            suggestedAddress: response.payload?.suggestedAddress,
                            userEnteredAddress: address,
                        })
                    );
                }
                return;
            }

            dispatch({
                meta: { actionTime: Date.now() },
                payload: response,
                type: UPDATE_ACCOUNT_SUCCESS,
            });

            if (toastMessage) {
                dispatch({
                    payload: toastMessage,
                    type: UPDATE_ACCOUNT_TOAST_MESSAGE,
                });
                dispatch({
                    payload: 0,
                    type: UPDATE_ACCOUNT_TOAST_MESSAGE,
                });
            }
            return dispatch(fetchBidderDataIfNeeded({ force: true }));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_ACCOUNT_FAIL,
            });
        }
    };

export const updateBidderAddressV2 =
    (bidderID: number, address: BidderAddress, verify = false) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        try {
            dispatch({
                type: UPDATE_ACCOUNT_REQUEST,
            });

            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            const response = await postUpdateBidderAddressV2({
                address,
                authToken,
                bidderID,
                deployment,
                verify,
            });

            const suggestionProvided = response.payload.suggestionProvided;

            if (suggestionProvided) {
                dispatch({
                    meta: { actionTime: Date.now() },
                    payload: response,
                    type: UPDATE_ACCOUNT_SUGGESTION,
                });
                return;
            }

            dispatch({
                meta: { actionTime: Date.now() },
                payload: response,
                type: UPDATE_ACCOUNT_SUCCESS,
            });
            return dispatch(fetchBidderDataIfNeeded({ force: true }));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_ACCOUNT_FAIL,
            });
        }
    };

export const addBidderAddressV2 =
    (bidderId: number, address: BidderAddress, verify = false, closeAfterVerification = false) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            dispatch({
                type: UPDATE_ACCOUNT_REQUEST,
            });

            const response = await postAddBidderAddressV2({
                address,
                authToken,
                bidderId,
                deployment,
                verify,
            });
            const suggestionProvided = response.payload.suggestionProvided;

            if (suggestionProvided) {
                const mostRecentAddress = response.payload.bidderAddresses.reduce((currentAddress, nextAddress) => {
                    if (!currentAddress.addressId) {
                        return nextAddress;
                    }
                    if (!nextAddress.addressId) {
                        return currentAddress;
                    }
                    if (currentAddress.addressId > nextAddress.addressId) {
                        return currentAddress;
                    }
                    return nextAddress;
                });
                const action: UpdateAccountSuggestionAction = {
                    error: false,
                    meta: { actionTime: Date.now() },
                    payload: {
                        ...response,
                        payload: {
                            ...response.payload,
                            originalAddress: {
                                ...address,
                                addressId: mostRecentAddress.addressId,
                            },
                        },
                    },
                    type: UPDATE_ACCOUNT_SUGGESTION,
                };
                dispatch(action);
                dispatch({
                    meta: { actionTime: Date.now() },
                    payload: response,
                    type: UPDATE_ACCOUNT_SUCCESS,
                });
                return;
            } else if (closeAfterVerification) {
                dispatch(dismissModal());
            }

            await dispatch(fetchBidderDataIfNeeded({ force: true }));
            const savedAddress =
                response.payload?.bidderAddresses?.length > 0 ? response.payload.bidderAddresses[0] : null;
            dispatch(storeSavedAddress(savedAddress));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_ACCOUNT_FAIL,
            });
        }
    };

export const addBidderAddress =
    (bidderID: number, address: BidderAddress, verify = false, shouldOpenModal = true) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            dispatch({
                type: UPDATE_ACCOUNT_REQUEST,
            });

            const response = await postAddBidderAddress({
                address,
                authToken,
                bidderID,
                deployment,
                verify,
            });
            if (!Array.isArray(response.payload)) {
                dispatch({
                    meta: { actionTime: Date.now() },
                    payload: response,
                    type: UPDATE_ACCOUNT_SUGGESTION,
                });

                if (shouldOpenModal) {
                    dispatch(
                        openAddressVerifyModal({
                            suggestedAddress: response.payload.suggestedAddress,
                            userEnteredAddress: address,
                        })
                    );
                }
                return;
            }
            dispatch({
                meta: { actionTime: Date.now() },
                payload: response,
                type: UPDATE_ACCOUNT_SUCCESS,
            });

            await dispatch(fetchBidderDataIfNeeded({ force: true }));
            const savedAddress = response.payload.length > 0 ? response.payload[0] : null;
            dispatch(storeSavedAddress(savedAddress));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_ACCOUNT_FAIL,
            });
        }
    };
